import React, { HTMLAttributes, useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import Refresh from "@material-ui/icons/Refresh";

import { EMPTY_STRING } from "common/constants";
import { useExamLockerContext } from "components/ExamLockerContext/ExamLockerContext";
import Loader from "components/Loader";
import TableCellWrapper from "components/VetExamsTable/TableCellWrapper";
import { EExamTableStatus } from "components/VetExamsTable/types";
import RevertDecisionDialog from "./RevertDecisionDialog";

import useStyles from "./styles";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  status: EExamTableStatus;
  isRevertDecisionLoading: boolean;
  shouldShowRevertButton: boolean;
  onConfirm: () => Promise<void>;
  horseName: string;
  eventName: string;
  eventDate: string;
  eventTabName: string;
  revertButtonDataTestId?: string;
  centered?: boolean;
}

const ExamStatusCell = (props: IProps) => {
  const {
    status,
    isRevertDecisionLoading,
    shouldShowRevertButton,
    horseName,
    eventName,
    eventDate,
    eventTabName,
    onConfirm,
    revertButtonDataTestId,
    centered = false,
    ...restProps
  } = props;

  const classes = useStyles();
  const { isLocked } = useExamLockerContext();

  const [isRevertDecisionDialogOpen, setIsRevertDecisionDialogOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenDecisionDialog = async () => {
    if (isLocked) return;

    setIsRevertDecisionDialogOpen(true);
  };

  const handleCloseDecisionDialog = () => {
    setIsRevertDecisionDialogOpen(false);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  let dialogTitle: string;
  let dialogContentText: string;

  if (isRevertDecisionDialogOpen) {
    if (status === EExamTableStatus.QUICK_PASSED) {
      dialogTitle = "Undo Quick Pass";
      dialogContentText = `Undoing the quick pass decision will result in the horse being placed back in ${eventTabName}`;
    } else {
      dialogTitle = "Undo Exam Request";
      dialogContentText = `Undoing the decision will result in the horse being placed back in ${eventTabName}`;
    }
  }

  if (isLoading || isRevertDecisionLoading) {
    return (
      <Loader className={classes.loader} size={14} disableGutters={true} />
    );
  }

  return (
    <TableCellWrapper {...restProps} centered={centered}>
      {status || EMPTY_STRING}
      {shouldShowRevertButton && !isLocked && (
        <IconButton
          className={classes.revertDecisionButton}
          onClick={handleOpenDecisionDialog}
          data-test-id={revertButtonDataTestId}
        >
          <Refresh fontSize={"small"} />
        </IconButton>
      )}
      {isRevertDecisionDialogOpen && (
        <RevertDecisionDialog
          title={dialogTitle}
          contentText={dialogContentText}
          horseName={horseName}
          eventDate={eventDate}
          eventName={eventName}
          onClose={handleCloseDecisionDialog}
          onConfirm={handleConfirm}
        />
      )}
    </TableCellWrapper>
  );
};

export default React.memo(ExamStatusCell);
